// Iter-418 — Instant-render stats snapshot.
//
// The homepage TrustBar ("Providers compared") and LiveSavingsTicker
// ("Drivers compared offers") both used to block on a cold `/api/stats`
// call before painting any figure — a visible multi-second lag on
// mobile, with a skeleton→fallback→live triple swap. Both surfaces now
// paint a snapshot synchronously and upgrade in place when the live
// aggregate resolves.
//
// Snapshot precedence:
//   1. `localStorage` copy of the last successful /api/stats payload.
//   2. BASELINE constants below — conservative floors that are never
//      higher than what the backend reports, preserving the
//      truthfulness contract documented in LiveSavingsTicker.jsx.
import { fetchStats } from "./api";

const STORAGE_KEY = "carsavr:stats-snapshot:v2";

export const STATS_BASELINE = {
  quoteRequests: 15000,
  lendersTracked: 36,
  avgSavings: 1100,
};

function pick(data) {
  const live = data?.live || {};
  const quoteRequests = Number.isFinite(live.drivers_compared_verified)
    ? live.drivers_compared_verified
    : null;
  return {
    quoteRequests,
    lendersTracked: Number.isFinite(live.lenders_tracked)
      ? live.lenders_tracked
      : null,
    avgSavings: Number.isFinite(data?.avg_savings_per_year)
      ? data.avg_savings_per_year
      : null,
  };
}

export function normalizeStats(data) {
  const p = pick(data);
  return {
    quoteRequests: p.quoteRequests ?? STATS_BASELINE.quoteRequests,
    lendersTracked: p.lendersTracked ?? STATS_BASELINE.lendersTracked,
    avgSavings: p.avgSavings ?? STATS_BASELINE.avgSavings,
    isLive: true,
  };
}

export function readStatsSnapshot() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const saved = JSON.parse(raw);
      if (Number.isFinite(saved?.quoteRequests) && Number.isFinite(saved?.lendersTracked)) {
        return {
          quoteRequests: saved.quoteRequests,
          lendersTracked: saved.lendersTracked,
          avgSavings: Number.isFinite(saved.avgSavings)
            ? saved.avgSavings
            : STATS_BASELINE.avgSavings,
          isLive: false,
        };
      }
    }
  } catch {
    /* private mode / storage disabled — fall through to baseline */
  }
  return { ...STATS_BASELINE, isLive: false };
}

function writeStatsSnapshot(snapshot) {
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...snapshot, savedAt: Date.now() }),
    );
  } catch {
    /* no-op */
  }
}

// One shared in-flight request per page load — TrustBar and
// LiveSavingsTicker previously fired two identical /api/stats calls.
let inflight = null;

export function fetchStatsShared() {
  if (!inflight) {
    inflight = fetchStats()
      .then((data) => {
        const snapshot = normalizeStats(data);
        writeStatsSnapshot(snapshot);
        return snapshot;
      })
      .catch((err) => {
        inflight = null;
        throw err;
      });
  }
  return inflight;
}
